/**
 * HUB-C — Onglet Paiements du Centre Activité.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Wallet } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import useActivitePaiements from '../../../hooks/activite/useActivitePaiements';
import useActivitePaiementActions from '../../../hooks/activite/useActivitePaiementActions';
import HistoriqueSumUpPendingPanel from '../../historique/HistoriqueSumUpPendingPanel';
import ActivitePaiementsKpiStrip from '../ActivitePaiementsKpiStrip';
import ActivitePaiementsSectionTabs from '../ActivitePaiementsSectionTabs';
import ActivitePaiementsStatusFilters from '../ActivitePaiementsStatusFilters';
import ActivitePaiementCard from '../ActivitePaiementCard';
import ActiviteStripePaiementCard from '../ActiviteStripePaiementCard';
import ActivitePaiementsProofCard from '../ActivitePaiementsProofCard';
import ActivitePaiementsTimeline from '../ActivitePaiementsTimeline';
import ActivitePaiementsRetraitsList from '../ActivitePaiementsRetraitsList';
import { ACTIVITE_TAB_IDS } from '../../../utils/activite/activityTabs';
import { PAIEMENTS_SECTIONS } from '../../../utils/paiements/paiementsDisplay';
import { activiteVentesLinkFromFilters } from '../../../utils/historique/ventesDisplay';

export default function ActivitePaiementsTab({ filters }) {
  const { api } = useAuth();
  const [activeSection, setActiveSection] = useState(PAIEMENTS_SECTIONS.TIMELINE);

  const {
    sumupPayments,
    stripePayments,
    pendingPayments,
    proofOrders,
    retraits,
    timeline,
    kpis,
    loading,
    error,
    sumupStatusFilter,
    setSumupStatusFilter,
    refresh,
  } = useActivitePaiements(api, filters);

  const onRefresh = useCallback(() => refresh(), [refresh]);

  const actions = useActivitePaiementActions({ api, onRefresh });

  const pendingSumup = useMemo(
    () => sumupPayments.filter((p) => ['PAID_PENDING', 'PENDING'].includes(p.status)),
    [sumupPayments],
  );
  const pendingStripe = useMemo(
    () => stripePayments.filter((p) => p.status === 'PENDING'),
    [stripePayments],
  );

  const sectionCounts = useMemo(() => ({
    [PAIEMENTS_SECTIONS.TIMELINE]: kpis.timelineCount,
    [PAIEMENTS_SECTIONS.SUMUP]: kpis.sumupCount,
    [PAIEMENTS_SECTIONS.STRIPE]: kpis.stripeCount,
    [PAIEMENTS_SECTIONS.PENDING]: pendingSumup.length + pendingStripe.length + (pendingPayments?.length || 0),
    [PAIEMENTS_SECTIONS.PROOFS]: proofOrders.length,
    [PAIEMENTS_SECTIONS.RETRAITS]: retraits.length,
  }), [kpis, pendingSumup, pendingStripe, pendingPayments, proofOrders, retraits]);

  const handleConfirmPending = useCallback(async (checkoutId) => {
    await actions.confirmPayment(checkoutId);
  }, [actions]);

  const handleCancelPending = useCallback(async (checkoutId) => {
    await actions.cancelPayment(checkoutId);
  }, [actions]);

  const renderSectionContent = () => {
    if (loading && activeSection !== PAIEMENTS_SECTIONS.PENDING) {
      return (
        <div className="flex justify-center py-12" data-testid="activite-paiements-loading">
          <div className="loading-spinner" />
        </div>
      );
    }

    switch (activeSection) {
      case PAIEMENTS_SECTIONS.TIMELINE:
        return (
          <ActivitePaiementsTimeline
            events={timeline}
            actions={actions}
            confirming={actions.confirming}
            cancelling={actions.cancelling}
            loading={loading}
          />
        );
      case PAIEMENTS_SECTIONS.SUMUP:
        return (
          <div className="space-y-4">
            <ActivitePaiementsStatusFilters value={sumupStatusFilter} onChange={setSumupStatusFilter} />
            {sumupPayments.length === 0 ? (
              <div className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-paiements-sumup-empty">
                Aucun paiement SumUp
              </div>
            ) : (
              <div className="space-y-3" data-testid="activite-paiements-sumup-list">
                {sumupPayments.map((p) => (
                  <ActivitePaiementCard
                    key={p.checkout_id}
                    payment={p}
                    actions={actions}
                    confirming={actions.confirming}
                    cancelling={actions.cancelling}
                  />
                ))}
              </div>
            )}
          </div>
        );
      case PAIEMENTS_SECTIONS.STRIPE:
        return stripePayments.length === 0 ? (
          <div className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-paiements-stripe-empty">
            Aucun paiement Stripe
          </div>
        ) : (
          <div className="space-y-3" data-testid="activite-paiements-stripe-list">
            {stripePayments.map((p) => (
              <ActiviteStripePaiementCard key={p.stripe_session_id || p.id} payment={p} />
            ))}
          </div>
        );
      case PAIEMENTS_SECTIONS.PENDING:
        return (
          <div className="space-y-4">
            <HistoriqueSumUpPendingPanel
              payments={pendingPayments}
              onConfirm={handleConfirmPending}
              onCancel={handleCancelPending}
              confirming={actions.confirming}
              cancelling={actions.cancelling}
              testIdPrefix="activite-paiements"
            />
            {pendingSumup.length === 0 && pendingStripe.length === 0 && !pendingPayments?.length ? (
              <div className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-paiements-pending-empty">
                Aucun paiement en attente
              </div>
            ) : (
              <div className="space-y-3">
                {pendingSumup.map((p) => (
                  <ActivitePaiementCard
                    key={`pending-sumup-${p.checkout_id}`}
                    payment={p}
                    actions={actions}
                    confirming={actions.confirming}
                    cancelling={actions.cancelling}
                  />
                ))}
                {pendingStripe.map((p) => (
                  <ActiviteStripePaiementCard key={`pending-stripe-${p.stripe_session_id || p.id}`} payment={p} />
                ))}
              </div>
            )}
          </div>
        );
      case PAIEMENTS_SECTIONS.PROOFS:
        return proofOrders.length === 0 ? (
          <div className="text-center py-12 text-[var(--rm-gray-500)]" data-testid="activite-paiements-proofs-empty">
            Aucune preuve de paiement
          </div>
        ) : (
          <div className="space-y-3" data-testid="activite-paiements-proofs-list">
            {proofOrders.map((order) => (
              <ActivitePaiementsProofCard key={order.id} order={order} actions={actions} />
            ))}
          </div>
        );
      case PAIEMENTS_SECTIONS.RETRAITS:
        return (
          <div className="space-y-4">
            <ActivitePaiementsRetraitsList retraits={retraits} loading={loading} />
            <p className="text-xs text-[var(--rm-gray-500)]">
              Les retraits espèces sont enregistrés depuis l&apos;onglet Ventes. Consultation uniquement ici.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="rounded-xl border p-4 sm:p-5 space-y-4"
      style={{ background: 'var(--rm-bg-elevated)', borderColor: 'var(--rm-border-subtle)' }}
      data-testid="activite-paiements-tab"
    >
      <div
        className="rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
        data-testid="activite-paiements-ventes-banner"
      >
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border"
            style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-elevated)' }}
          >
            <Wallet className="w-5 h-5 text-[var(--rm-gray-200)]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--rm-white)]">Retraits & encaissements caisse</p>
            <p className="text-xs text-[var(--rm-gray-500)] mt-0.5">
              Enregistrement des retraits depuis l&apos;onglet Ventes — historique ventes inchangé sur /historique.
            </p>
          </div>
        </div>
        <Link
          to={activiteVentesLinkFromFilters(filters)}
          className="btn-secondary px-4 py-2.5 min-h-[44px] text-sm inline-flex items-center justify-center gap-2 shrink-0 w-full sm:w-auto"
          data-testid="activite-paiements-link-ventes"
        >
          Onglet Ventes
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {activeSection !== PAIEMENTS_SECTIONS.PENDING && (
        <HistoriqueSumUpPendingPanel
          payments={pendingPayments}
          onConfirm={handleConfirmPending}
          onCancel={handleCancelPending}
          confirming={actions.confirming}
          cancelling={actions.cancelling}
          testIdPrefix="activite-paiements"
        />
      )}

      <ActivitePaiementsKpiStrip kpis={kpis} />

      <ActivitePaiementsSectionTabs
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        counts={sectionCounts}
      />

      {error && (
        <div
          className="rounded-xl border p-4 text-sm text-[var(--rm-red-light)]"
          style={{ borderColor: 'var(--rm-border-strong)', background: 'var(--rm-red-muted)' }}
          data-testid="activite-paiements-error"
        >
          {error}
        </div>
      )}

      {renderSectionContent()}

      <span className="sr-only" data-testid="activite-paiements-tab-id">{ACTIVITE_TAB_IDS.PAIEMENTS}</span>
    </div>
  );
}
